/**
 * NewBookDialog.tsx — 新建书籍弹窗（books 域）· 已接真（ADM-BOK-S003）
 *
 * 移植自原型 design/Novel-admin/src/dialogs.jsx 674–833（注册名 new-book）；保留全屏三栏布局/样式。
 * 接真：录入 → booksApi.create(CreateNovelRequest) 创建书组 root per-locale 行（默认「草稿」HIDDEN，章节稍后单独创建）。
 *   - 封面（必填）：选文件 → booksApi.uploadCover（写共享内容桶 + 返相对 ref）→ 随 create 写 cover_url，本地即时预览。
 *   - 分类（必填，UUID）/ 标签（UUID[]）：useCategoriesList / useTagsList 真数据；快捷创建走 CategoryEditor（真 API）后
 *     invalidate 列表刷新再选。
 *   - slug（必填，[a-z0-9-]）/ 简介（必填）；原版 = 新书自身（original_title=title / original_locale=lang）。
 *   - 中文运营识别名 cn_title（必填，A6 §16.1 / dd#970）：写 `novel_groups.cn_title`，**只在 admin 面用**，
 *     与「书组下有没有中文语言版」无关。首版语言选中文时可与书名重合，但仍是两个字段（原作非中文的
 *     批次进来时不重合），故独立录入、不做自动同步 —— 自动同步会让运营改了书名却不知道识别名也变了。
 *   - 来源：原创=ORIGINAL；外部采集 (tentoo)=IMPORTED + source_provider=TENTOO + source_external_id。
 *     建书是来源维护面，operator+ 可选择来源；浏览面来源展示仍按 ADM-BOK-S004 限 root。
 *   - 年龄：全年龄/12+/16+/18+ → ALL_AGES/TEEN_13/MATURE_16/ADULT_18。
 *   - 改写版本（ADM-BOK-S024，可选）：trim 后非空才下发 edition_label（1..32）；留空 = 原版、不带该键。
 * 成功 → toast + invalidate(['books']) + 关闭；失败 → 错误 toast 不假成功。viewer/无写权由后端 requireRole 兜底。
 */

import type { CreateNovelRequest } from '@novel-admin/shared';
import {
  EDITION_LABEL_MAX_LENGTH,
  isTranslationFactoryProviderCode,
  parseEditionLabelDraft,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useCategoriesList, useSysConfig, useTagsList } from '~/data/queries';
import { booksApi } from '~/hooks/use-books';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import { useCanWriteBooks } from '../book-rbac';
import { CategoryEditor } from './CategoryEditor';

/**
 * 弹窗内 FormRow（140px 标签 + 字段，底部分隔线）— 复用共享 ~/ui/FormRow，
 * 逐属性对齐原型 dialogs.jsx：labelWidth=140 / gap=14 / pad="12px 0" / labelPadTop=6。
 */
const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** 年龄分级展示 → AgeRatingSchema 枚举 */
const AGE_OPTIONS: { label: string; value: CreateNovelRequest['age_rating'] }[] = [
  { label: '全年龄', value: 'ALL_AGES' },
  { label: '12+', value: 'TEEN_13' },
  { label: '16+', value: 'MATURE_16' },
  { label: '18+', value: 'ADULT_18' },
];

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/i;

/** 内嵌 category-editor 唤起描述：分类 / 标签，附 onCreated 回填（创建真 API 后 invalidate 列表）*/
type CatEditorReq =
  | { kind: 'cats'; onCreated: (name: string) => void }
  | { kind: 'tag'; onCreated: (name: string) => void };

export function NewBookDialog({ onClose }: { onClose: () => void }) {
  const { data: sys } = useSysConfig();
  const { data: categories } = useCategoriesList();
  const { data: tags } = useTagsList();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  /** 中文运营识别名（书组级，A6 §16.1 必填）*/
  const [cnTitle, setCnTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [author, setAuthor] = useState('');
  /** ADM-BOK-S024 改写版本标签草稿（空 = 原版）*/
  const [editionLabel, setEditionLabel] = useState('');
  const [lang, setLang] = useState('en');
  const [categoryId, setCategoryId] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [age, setAge] = useState<CreateNovelRequest['age_rating']>('ALL_AGES');
  const [free, setFree] = useState(5);
  const [isRecommended, setIsRecommended] = useState(false);
  const [source, setSource] = useState<'原创' | 'tentoo'>('原创');
  const [sourceId, setSourceId] = useState('');
  const [description, setDescription] = useState('');
  // 封面：上传后存相对 ref（必填）；本地 objectURL 即时预览
  const [coverUrl, setCoverUrl] = useState('');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  // 内嵌 category-editor 受控 open state（替代原型 window.openDialog hub）
  const [catEditor, setCatEditor] = useState<CatEditorReq | null>(null);

  const coverMut = useMutation({
    mutationFn: (file: File) => booksApi.uploadCover(file),
    onSuccess: (res, file) => {
      setCoverUrl(res.cover_url);
      // 重选时先 revoke 旧 objectURL，避免 blob URL 泄漏（adversarial）
      setCoverPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      toast('封面已上传', { tone: 'pos', icon: 'IconCheck' });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '封面上传失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });

  const allLangs = sys.languages;
  const curLangZh =
    allLangs.find((l) => l.language_code === lang)?.display_name_i18n.zh || lang.toUpperCase();

  const slugValid = SLUG_RE.test(slug.trim());
  // 建书属于 operator+ 来源维护面，不能复用列表/详情浏览面的 root-only 门控。
  const canWriteBooks = useCanWriteBooks();
  const isImported = canWriteBooks && source === 'tentoo';
  const editionLabelDraft = parseEditionLabelDraft(editionLabel);
  // 工厂译制集合成员不表达自己的标签（§16.1 提交即拒）：判定与服务端写入闸同源
  const editionLabelFollowsBase = isTranslationFactoryProviderCode(isImported ? 'TENTOO' : null);
  const canCreate =
    canWriteBooks &&
    title.trim().length > 0 &&
    cnTitle.trim().length > 0 &&
    slugValid &&
    description.trim().length > 0 &&
    !!coverUrl &&
    !!categoryId &&
    (!isImported || sourceId.trim().length > 0) &&
    editionLabelDraft.ok &&
    !coverMut.isPending;

  const createMut = useMutation({
    mutationFn: () => {
      const body: CreateNovelRequest = {
        slug: slug.trim(),
        locale: lang,
        title: title.trim(),
        description: description.trim(),
        cover_url: coverUrl,
        // 中文运营识别名（书组级，dd#970）；与 original_title 是两个字段，不自动同步
        cn_title: cnTitle.trim(),
        // 新书原版 = 自身（书组第一行；后续可在多语言版本里维护其它 locale）
        original_title: title.trim(),
        original_locale: lang,
        author_display: author.trim() === '' ? null : author.trim(),
        // S024：留空 = 原版，不带该键（§16.1 缺省 NULL）；成员行不下发
        ...(!editionLabelFollowsBase && editionLabelDraft.ok && editionLabelDraft.value !== null
          ? { edition_label: editionLabelDraft.value }
          : {}),
        source: isImported ? 'IMPORTED' : 'ORIGINAL',
        age_rating: age,
        category_id: categoryId,
        tags: tagIds,
        free_chapter_count: free,
        is_recommended: isRecommended,
        ...(isImported
          ? { source_provider: 'TENTOO' as const, source_external_id: sourceId.trim() }
          : {}),
      };
      return booksApi.create(body);
    },
    onSuccess: async () => {
      toast('书籍已创建（草稿）', { tone: 'pos', icon: 'IconCheck' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
        queryClient.invalidateQueries({ queryKey: ['tags'] }),
      ]);
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '创建失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  const toggleTag = (id: string) =>
    setTagIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <>
      <Dialog
        width={760}
        title="新建书籍"
        subtitle="录入书籍基础信息 · 创建后默认为「草稿」状态，仅运营可见，章节稍后单独创建"
        icon="IconBook"
        onClose={onClose}
        footer={
          <>
            <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
              <Icons.IconEdit
                size={11}
                style={{ verticalAlign: 'middle', marginRight: 4, color: 'var(--info)' }}
              />
              创建后进入「草稿」状态，需在书籍详情 · 上下架 tab 显式发布上架后客户端才可见
            </div>
            <button className="btn" onClick={onClose}>
              取消
            </button>
            <button
              className="btn btn-accent"
              disabled={!canCreate || createMut.isPending}
              title={
                createMut.isPending
                  ? '创建中…'
                  : !title.trim()
                    ? '书名必填'
                    : !cnTitle.trim()
                      ? '中文识别名必填'
                      : !slugValid
                        ? 'slug 必填且仅限字母/数字/连字符'
                        : !coverUrl
                          ? '请先上传封面'
                          : !categoryId
                            ? '请选择分类'
                            : !description.trim()
                              ? '简介必填'
                              : isImported && !sourceId.trim()
                                ? '外部采集需填来源书籍 ID'
                                : '创建书籍'
              }
              onClick={() => canCreate && createMut.mutate()}
            >
              {createMut.isPending ? '创建中…' : '创建'}
            </button>
          </>
        }
      >
        <div style={{ padding: 18, display: 'grid', gridTemplateColumns: '140px 1fr', gap: 24 }}>
          <div>
            <div className="label" style={{ marginBottom: 8 }}>
              封面 *
            </div>
            <label
              title="上传封面（≤2MB · webp/png/jpeg）"
              style={{
                width: 130,
                height: 180,
                borderRadius: 6,
                background: coverPreview
                  ? `center/cover no-repeat url(${coverPreview})`
                  : 'linear-gradient(160deg, oklch(0.55 0.12 50), oklch(0.32 0.08 30))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 6,
                color: 'rgba(255,255,255,0.85)',
                border: '1px dashed rgba(255,255,255,0.3)',
                cursor: coverMut.isPending ? 'wait' : 'pointer',
                textShadow: coverPreview ? '0 1px 4px rgba(0,0,0,0.6)' : undefined,
              }}
            >
              <Icons.IconUpload size={20} />
              <span style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)' }}>
                {coverMut.isPending ? '上传中…' : coverPreview ? '重新选择' : '上传封面'}
              </span>
              <span style={{ fontSize: 9, opacity: 0.7 }}>640×880</span>
              <input
                type="file"
                accept="image/webp,image/png,image/jpeg"
                hidden
                disabled={coverMut.isPending}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) coverMut.mutate(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <div>
            <FormRow label="首版语言" required>
              <Select
                className="input"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                style={{ maxWidth: 260 }}
              >
                {allLangs.map((l) => (
                  <option key={l.language_code} value={l.language_code}>
                    {l.language_code.toUpperCase()} · {l.display_name_i18n.zh}
                    {!l.enabled ? '（未开启）' : ''}
                  </option>
                ))}
              </Select>
            </FormRow>
            <FormRow label={`书名（${curLangZh}）`} required>
              <input
                className="input"
                placeholder={`书名（${curLangZh}）`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormRow>
            <FormRow label="中文识别名" required hint="运营内部认书用 · 只在后台显示，客户端不下发">
              <input
                className="input"
                placeholder="中文识别名（如：星辰与海）"
                value={cnTitle}
                onChange={(e) => setCnTitle(e.target.value)}
              />
            </FormRow>
            <FormRow label="slug" required hint="唯一标识 · 字母/数字/连字符（如 my-book）">
              <input
                className="input mono"
                placeholder="my-book"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                style={{ maxWidth: 280 }}
              />
            </FormRow>
            <FormRow label="作者">
              <input
                className="input"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                style={{ maxWidth: 280 }}
              />
            </FormRow>
            <FormRow label="改写版本" hint="区分同一作品的不同改写本（如 A / B）· 仅后台可见">
              <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
                <input
                  aria-label="改写版本"
                  className="input"
                  value={editionLabel}
                  onChange={(e) => setEditionLabel(e.target.value)}
                  placeholder="留空 = 原版"
                  disabled={editionLabelFollowsBase}
                  style={{ maxWidth: 200 }}
                />
                {!editionLabelDraft.ok && (
                  <span style={{ fontSize: 11, color: 'var(--neg)' }}>
                    最多 {EDITION_LABEL_MAX_LENGTH} 个字符
                  </span>
                )}
              </div>
            </FormRow>
            <FormRow label="分类" required hint="单选 / 快捷创建">
              <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCategoryId(c.id)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      font: 'inherit',
                      border:
                        categoryId === c.id ? '1px solid var(--accent)' : '1px solid var(--border)',
                      background: categoryId === c.id ? 'var(--accent-soft)' : 'var(--bg-elev)',
                      color: categoryId === c.id ? 'var(--accent-fg)' : 'var(--fg-muted)',
                      fontSize: 11.5,
                      cursor: 'pointer',
                    }}
                  >
                    {c.name}
                  </button>
                ))}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ height: 26, padding: '0 8px' }}
                  onClick={() =>
                    setCatEditor({
                      kind: 'cats',
                      onCreated: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
                    })
                  }
                  title="新建分类（多语言）"
                >
                  <Icons.IconPlus size={10} />
                </button>
              </div>
            </FormRow>
            <FormRow label="标签" hint="可多选 / 快捷创建">
              <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                {tags.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggleTag(t.id)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      font: 'inherit',
                      border: tagIds.includes(t.id)
                        ? '1px solid var(--accent)'
                        : '1px solid var(--border)',
                      background: tagIds.includes(t.id) ? 'var(--accent-soft)' : 'var(--bg-elev)',
                      color: tagIds.includes(t.id) ? 'var(--accent-fg)' : 'var(--fg-muted)',
                      fontSize: 11.5,
                      cursor: 'pointer',
                    }}
                  >
                    {t.name}
                  </button>
                ))}
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ height: 26, padding: '0 8px' }}
                  onClick={() =>
                    setCatEditor({
                      kind: 'tag',
                      onCreated: () => queryClient.invalidateQueries({ queryKey: ['tags'] }),
                    })
                  }
                  title="新建标签（多语言）"
                >
                  <Icons.IconPlus size={10} />
                </button>
              </div>
            </FormRow>
            <FormRow label="年龄限制">
              <Select
                className="input"
                value={age}
                onChange={(e) => setAge(e.target.value as CreateNovelRequest['age_rating'])}
                style={{ maxWidth: 180 }}
              >
                {AGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormRow>
            <FormRow label="简介" required>
              <textarea
                className="input"
                rows={3}
                placeholder="书籍简介…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ height: 'auto' }}
              />
            </FormRow>
            <FormRow label="推荐标记" hint="仅首个语言版本 · 优先参与算法选书">
              <label className="hstack" style={{ gap: 8, fontSize: 12 }}>
                <input
                  type="checkbox"
                  aria-label="推荐标记"
                  checked={isRecommended}
                  onChange={(event) => setIsRecommended(event.target.checked)}
                />
                {isRecommended ? '已推荐' : '未推荐'}
              </label>
            </FormRow>
            <FormRow label="免费章节数" hint="默认 5 · 范围 0–1000 · 创建后可在书籍信息中调整">
              <input
                className="input mono"
                value={free}
                onChange={(e) => setFree(Math.max(0, Number(e.target.value) || 0))}
                style={{ width: 100, textAlign: 'right' }}
              />
            </FormRow>
            {/* ADM-BOK-S004：建书维护面向 operator+ 展示来源，不影响浏览面的角色门控。*/}
            {canWriteBooks && (
              <FormRow label="内容来源">
                <div className="seg">
                  <button
                    className={source === '原创' ? 'on' : ''}
                    onClick={() => setSource('原创')}
                  >
                    原创
                  </button>
                  <button
                    className={source === 'tentoo' ? 'on' : ''}
                    onClick={() => setSource('tentoo')}
                  >
                    外部采集 (tentoo)
                  </button>
                </div>
                {isImported && (
                  <div className="hstack" style={{ marginTop: 10, gap: 8 }}>
                    <input
                      className="input mono"
                      placeholder="来源外部书籍 ID (source_external_id) · tt-xxxxx"
                      value={sourceId}
                      onChange={(e) => setSourceId(e.target.value)}
                      style={{ maxWidth: 320 }}
                    />
                    <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      系统将按周自动同步新章节
                    </span>
                  </div>
                )}
              </FormRow>
            )}
          </div>
        </div>
      </Dialog>
      {catEditor && (
        <CategoryEditor
          kind={catEditor.kind}
          onCreated={catEditor.onCreated}
          onClose={() => setCatEditor(null)}
        />
      )}
    </>
  );
}
